import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { observer } from "mobx-react";
import React, { useEffect, useRef, memo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { addDisplayName } from "../Infra/display-name";
import { AIAssistantViewModel } from "./AIAssistantViewModel";
import { HXTextField } from "./base.styles";
import { AIAssistantParent, MessageContainer, AIClearButton, Spinner, LeftContainer, RightContainer, QueryContainer, AssistantInfo, HelpText, ExampleHelpText, SampleQuestionContainer, Question, Message, MessageWrapper, NoChatsMessage, ChatHistoryHeader, ConversationList, ConversationItem, ConversationTitle, ConversationTimestamp, AddConversationButton, AddConversationIcon, EditConversationIcon, ConversationTitleInput, DeleteConversationIcon, MetadataContainer, LoadMoreButton, MessageActionsRow, CopyActionIcon, } from "./AIAssistantView.styles";
import { useViewModel } from "./useViewModel";
import InputAdornment from "@mui/material/InputAdornment";
import { useFormHelper } from "./FormHelper";
import { FilePreviewDialog } from "./FileUploadView.styles";
import { MultiPagePDFViewer } from "./MutiPagePDFViewer";
import rehypeRaw from "rehype-raw";
import { MessageRole } from "healthex-common/aiassistant.dtos";
import { Element, Events, scroller } from "react-scroll";
const safeDecodeURI = (uri) => {
    try {
        return decodeURIComponent(uri);
    }
    catch (e) {
        return uri;
    }
};
const createMarkdownComponents = (handleLinkClick) => ({
    a: ({ href, className, children, ...linkProps }) => {
        if (className === "s3-link" && linkProps["data-source-path"]) {
            return (_jsxs(_Fragment, { children: ["-", " ", _jsx("em", { children: _jsx("a", { href: "#", "data-source-path": linkProps["data-source-path"], onClick: (e) => handleLinkClick(e, linkProps["data-source-path"]), style: { textDecoration: "none" }, children: children }) })] }));
        }
        return (_jsx("a", { href: href, ...linkProps, children: children }));
    },
});
const MessageContent = memo(({ content, metadata, messageId, handleLinkClick, onLoadMore, }) => {
    var _a;
    const fixedContent = content.replace(/\[Source:\s*(s3:\/\/[^\]]+)]|\b(s3:\/\/[^\s\]]+)/g, (_, path1, path2) => {
        var _a;
        const originalPath = safeDecodeURI(path1 || path2 || "");
        const [pathWithoutPage, pageNumber] = originalPath.split("#page=");
        const cleanPath = pathWithoutPage
            .replace(/-\d+(?=\.pdf|\.pdf-\d+)/g, "")
            .replace(/\.pdf-\d+/, ".pdf")
            .replace(/_$/, "");
        const fileName = (_a = cleanPath.split("/").pop()) !== null && _a !== void 0 ? _a : "document.pdf";
        return `<a href="${cleanPath}${pageNumber ? `#page=${pageNumber}` : ""}" data-source-path="${originalPath}" class="s3-link">${fileName}</a>`;
    });
    const showLoadMore = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.pagination) === null || _a === void 0 ? void 0 : _a.hasMoreResults;
    return (_jsxs(_Fragment, { children: [_jsx(ReactMarkdown, { rehypePlugins: [rehypeRaw], components: createMarkdownComponents(handleLinkClick), children: fixedContent }), showLoadMore && (_jsx(LoadMoreButton, { onClick: () => onLoadMore(messageId, metadata.pagination), children: "Load More Results" })), (metadata === null || metadata === void 0 ? void 0 : metadata.footer) && (_jsx(MetadataContainer, { children: _jsx("div", { children: `${metadata.footer.type === "warning" ? "🧠" : "✔️"} ${metadata.footer.text}` }) }))] }));
});
const ConversationListComponent = memo(({ conversations, activeId, editingId, editingTitle, onSelect, onEdit, onDelete, onSaveTitle, onCancelEdit, onTitleChange, }) => {
    return (_jsx(ConversationList, { children: conversations.length > 0 ? (conversations.map((conversation) => (_jsx(ConversationItemComponent, { conversation: conversation, isActive: conversation.id === activeId, isEditing: conversation.id === editingId, editingTitle: editingTitle, onSelect: onSelect, onEdit: onEdit, onDelete: onDelete, onSaveTitle: onSaveTitle, onCancelEdit: onCancelEdit, onTitleChange: onTitleChange }, conversation.id)))) : (_jsx(NoChatsMessage, { children: "No conversations yet" })) }));
});
const ConversationItemComponent = memo(({ conversation, isActive, isEditing, editingTitle, onSelect, onEdit, onDelete, onSaveTitle, onCancelEdit, onTitleChange, }) => (_jsxs(ConversationItem, { isActive: isActive, onClick: () => onSelect(conversation.id), children: [_jsx(ConversationTitle, { children: isEditing ? (_jsx(ConversationTitleInput, { autoFocus: true, value: editingTitle, onChange: (e) => onTitleChange(e.target.value), onBlur: onSaveTitle, onKeyDown: (e) => {
                    if (e.key === "Enter")
                        onSaveTitle();
                    if (e.key === "Escape")
                        onCancelEdit();
                }, onClick: (e) => e.stopPropagation() })) : (_jsxs(_Fragment, { children: [conversation.title || "New Chat", _jsx(EditConversationIcon, { className: "edit-icon", onClick: (e) => {
                            e.stopPropagation();
                            onEdit(conversation.id, conversation.title);
                        } }), _jsx(DeleteConversationIcon, { className: "delete-icon", onClick: (e) => {
                            e.stopPropagation();
                            onDelete(conversation.id);
                        } })] })) }), _jsx(ConversationTimestamp, { children: new Date(conversation.updatedAt).toLocaleString() })] }, conversation.id)));
const messagesContainerId = "messagesContainer";
const messagesEndName = "messagesEnd";
export const AIAssistantView = observer((props) => {
    const model = useViewModel(AIAssistantViewModel, props);
    const formModel = model.formModel;
    const messagesContainerRef = useRef(null);
    const [lastScrollTop, setLastScrollTop] = React.useState(0);
    const [canAutoScroll, setCanAutoScroll] = React.useState(true);
    const [isScrollingToBottom, setIsScrollingToBottom] = React.useState(false);
    const [queueScrollingToBottom, setQueueScrollingToBottom] = React.useState(false);
    const canScrollToBottom = (isAutoScroll) => {
        return (isAutoScroll && canAutoScroll) || !isAutoScroll;
    };
    const scrollToBottom = (isAutoScroll, scrollBehavior) => {
        if (!canScrollToBottom(isAutoScroll))
            return;
        scroller.scrollTo(messagesEndName, {
            containerId: messagesContainerId,
            offset: 0,
            duration: scrollBehavior === "instant" ? 0 : 100,
            smooth: scrollBehavior === "smooth",
        });
    };
    const handleScrollToBottom = (isAutoScroll, scrollBehavior) => {
        if (!canScrollToBottom(isAutoScroll))
            return;
        if (isScrollingToBottom) {
            setQueueScrollingToBottom(true);
            return;
        }
        setIsScrollingToBottom(true);
        setTimeout(() => scrollToBottom(isAutoScroll, scrollBehavior), 100);
    };
    useEffect(() => {
        Events.scrollEvent.register("end", (to, _element) => {
            if (to === "messagesEnd") {
                setIsScrollingToBottom(false);
                if (queueScrollingToBottom) {
                    setQueueScrollingToBottom(false);
                    handleScrollToBottom(true, "instant");
                }
            }
        });
        return () => {
            Events.scrollEvent.remove("end");
        };
    });
    useEffect(() => {
        const handleScroll = () => {
            const element = messagesContainerRef.current;
            if (!element)
                return;
            const { scrollHeight, scrollTop, clientHeight } = element;
            const isUp = scrollTop < lastScrollTop;
            const isBottom = scrollHeight - scrollTop === clientHeight;
            const autoScroll = canAutoScroll ? !isUp || isBottom : isBottom;
            setCanAutoScroll(autoScroll);
            setLastScrollTop(scrollTop);
        };
        if (messagesContainerRef.current) {
            messagesContainerRef.current.addEventListener("scroll", handleScroll);
        }
        return () => {
            if (messagesContainerRef.current) {
                messagesContainerRef.current.removeEventListener("scroll", handleScroll);
            }
        };
    });
    useEffect(() => {
        if (model.activeConversationId && model.messageStreamingKeyInfo && canAutoScroll) {
            const { isAutoScroll, scrollBehavior } = model.messageStreamingKeyInfo;
            handleScrollToBottom(isAutoScroll, scrollBehavior);
        }
    }, [model.activeConversationId, model.messageStreamingKeyInfo]);
    const formHelper = useFormHelper(formModel);
    const handleLinkClick = useCallback((e, href) => {
        e.preventDefault();
        e.stopPropagation();
        const pageMatch = href.match(/#page=(\d+)/);
        const pageNumber = pageMatch ? parseInt(pageMatch[1]) : 1;
        const cleanHref = href.split("#")[0];
        if (cleanHref.includes("/studyProtocols/")) {
            void model.getStudyProtocolDocumentDownloadUrl(cleanHref, pageNumber);
        }
        else if (cleanHref.includes("/iCFProtocols/")) {
            void model.getICFFormDownloadUrl(cleanHref, pageNumber);
        }
        else {
            console.error("Unknown protocol type in path:", cleanHref);
        }
    }, [model]);
    const handleNewConversation = useCallback(() => model.createNewConversation(), [model]);
    const handleQuerySubmit = async () => {
        if (!model.activeConversationId && formModel.input.query) {
            const conversation = await model.createNewConversation(formModel.input.query);
            await model.loadConversationMessages(conversation.id);
        }
        setCanAutoScroll(true);
        await model.sendQuery();
    };
    return (_jsxs(AIAssistantParent, { children: [_jsxs(LeftContainer, { children: [_jsx(ChatHistoryHeader, { children: "Recent Chats" }), _jsx(ConversationListComponent, { conversations: model.conversations, activeId: model.activeConversationId, editingId: model.editingConversationId, editingTitle: model.editingTitle, onSelect: useCallback((id) => model.setActiveConversation(id), [model]), onEdit: useCallback((id, title) => model.startEditing(id, title), [model]), onDelete: useCallback((id) => model.deleteConversation(id), [model]), onSaveTitle: useCallback(() => model.saveConversationTitle(), [model]), onCancelEdit: useCallback(() => model.cancelEditing(), [model]), onTitleChange: useCallback((value) => model.setEditingTitle(value), [model]) }), _jsxs(AddConversationButton, { onClick: handleNewConversation, children: [_jsx(AddConversationIcon, {}), " New Conversation"] })] }), _jsxs(RightContainer, { children: [_jsxs(MessageContainer, { ref: messagesContainerRef, id: messagesContainerId, children: [model.messages.length === 0 && !model.activeConversationId && (_jsxs(AssistantInfo, { children: [_jsx(HelpText, { children: "Ask me anything related to your research studies or patient consents" }), _jsx(ExampleHelpText, { children: "Examples of what to ask" }), _jsxs(SampleQuestionContainer, { children: [_jsx(Question, { children: "\"List all patient consents for a study?\" \u2192" }), _jsx(Question, { children: "\"Has the patient P1 consented to any procedure related to blood draws?\" \u2192" }), _jsx(Question, { children: "\"Show the consent proof of a particular consent\" \u2192" })] })] })), model.activeConversationId &&
                                model.messages.map((message, index) => (_jsxs(MessageWrapper, { role: message.role, children: [_jsxs(Message, { role: message.role, children: [message.role === MessageRole.AI && message.status === "inProgress" && !message.content && _jsx(Spinner, {}), _jsx(MessageContent, { content: message.content, metadata: message.metadata, messageId: message.id, handleLinkClick: handleLinkClick, onLoadMore: () => { var _a; return ((_a = message.metadata) === null || _a === void 0 ? void 0 : _a.pagination) && model.handleLoadMore(message.metadata.pagination); } })] }), _jsx(MessageActionsRow, { role: message.role, children: _jsx(CopyActionIcon, { onClick: () => copyToClipboard(message.content, "Message copied to clipboard!") }) })] }, index))), _jsx(Element, { name: messagesEndName })] }), _jsx(QueryContainer, { children: _jsx(HXTextField, { style: { width: "96%" }, ...formHelper.bindTextField("query"), placeholder: "Type your message here...", onKeyDown: async (e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    await handleQuerySubmit();
                                }
                            }, InputProps: {
                                endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(AIClearButton, { onClick: handleQuerySubmit }) })),
                            } }) })] }), _jsx(FilePreviewDialog, { open: !!model.protocolDocumentUrl, onClose: () => model.closeProtocolDocumentDownloadUrl(), buttons: [], closeChoice: false, title: `Preview: ${model.protocolDocumentName}`, children: model.protocolDocumentUrl && _jsx(MultiPagePDFViewer, { url: model.protocolDocumentUrl, initialPage: model.currentPageNumber }) })] }));
    async function copyToClipboard(value, successMessage) {
        await navigator.clipboard.writeText(value);
        model.showCopySuccess(successMessage);
    }
});
addDisplayName(AIAssistantView, "AIAssistantView");
